import { getMetricValue, getProfitValue } from '../../helpers/content/content';
import { game } from '../../setup/game';
import { metrics } from '../../setup/general';

export function getCostOffsetValue(row: number, offset: number) {
    const n = 5 - Math.ceil(Math.abs(row - 4.5));
    const m = row >= 5 ? -1 : 1;
    return Math.max(0, m * ((offset > n ? offset - n : 0) || (offset < -n ? offset + n : 0)) + 10);
}

export function getElementAttraction(elementNames: string[], min = 0) {
    // Get shuffled list of element names
    const list = elementNames.slice();
    list.sort(() => (Math.random() < 0.5 ? -1 : 1));

    // Add same element names until min
    if (list.length < min) {
        while (list.length < min) {
            const end = Math.min(list.length, min - list.length);
            const elements = list.slice(0, end);
            list.push(...elements);
        }
    }

    // Shuffle final list
    list.sort(() => (Math.random() < 0.5 ? -1 : 1));

    return list;
}

export function getElementPosition(type: 'bucket' | 'cost' | 'gate' | 'pin', row = 0, column = 0): Point {
    const { offset, padding, spacing, width } = game.board;

    // Prepare type offset
    const typeOffset = {
        x: 0,
        y: 0,
    };

    // Check type
    if (type === 'bucket') {
        row = game.rows;
        typeOffset.y += spacing.y;
    } else if (type === 'cost') {
        column = row;
        typeOffset.x = spacing.x / 2;
    } else if (type === 'gate') {
        typeOffset.y = 7;
    } else if (type === 'pin') {
        typeOffset.x = spacing.x / 2;
    }

    // Return position
    return {
        x: Math.round(spacing.x * (column + 1 - (row + 2) / 2) + width / 2 + offset.x + typeOffset.x),
        y: Math.round(spacing.y * row + padding + offset.y + typeOffset.y),
    };
}

export function splitElementName(elementName: string) {
    const [row, column] = elementName.split('-');
    return {
        column: column ? parseInt(column) : 0,
        row: row ? parseInt(row) : 0,
    };
}

export function getGatesTypes(gates: any[]) {
    const types: Record<string, GateType> = {};
    gates.forEach(gate => (types[gate.name] = gate.type));
    return types;
}

export function getGatesStates(
    gatesTypes: GatesTypes,
    costValues: number[],
    impactValues: number[],
    metricName: MetricName
) {
    const metric = metrics[metricName];

    // Initialize gate state list and mapping
    const mapping: GatesStates = {};
    const list: GateState[] = Object.entries(gatesTypes).map(([gateName, gateType]) => {
        // Get gate column and row
        const { column, row } = splitElementName(gateName);

        // Create gate state
        const gateState = {
            blocked: false,
            bounce: gateType === 'bounce',
            closed: gateType === 'closed',
            column,
            costValue: costValues[row],
            expectedCosts: 0,
            expectedImpact: 0,
            expectedImpactLeft: 0,
            expectedImpactRight: 0,
            expectedMetricValue: 0,
            expectedProfit: 0,
            name: gateName,
            open: gateType === 'open',
            row,
            type: gateType,
        };

        // Add to mapping and list
        mapping[gateName] = gateState;
        return gateState;
    });

    // Get blocked states based on gates above, skipping first three
    for (let i = 3; i < list.length; i++) {
        const gateState = list[i];

        // Get above gate states
        const aboveLeft = mapping[`${gateState.row - 1}-${gateState.column - 1}`];
        const aboveRight = mapping[`${gateState.row - 1}-${gateState.column}`];

        // Get blocked
        const leftBlocked = !aboveLeft || aboveLeft.blocked || !aboveLeft.open;
        const rightBlocked = !aboveRight || aboveRight.blocked || !aboveRight.open;
        gateState.blocked = leftBlocked && rightBlocked;
    }

    // Get expected impact values based on gates below
    for (let i = list.length - 1; i >= 0; i--) {
        const gateState = list[i];

        if ((!gateState.blocked && gateState.open) || !i) {
            // Get gates below
            const belowLeft: GateState | undefined = mapping[`${gateState.row + 1}-${gateState.column}`];
            const belowRight: GateState | undefined = mapping[`${gateState.row + 1}-${gateState.column + 1}`];

            // Get expected costs
            if (gateState.open || !i) {
                gateState.expectedCosts = gateState.costValue;
            }

            // Get expected impact from gates below or impact values
            if (belowLeft || belowRight) {
                // Get multipliers
                const leftMultiplier = belowRight?.bounce ? 0.75 : 0.5;
                const rightMultiplier = belowLeft?.bounce ? 0.75 : 0.5;

                // From gates
                gateState.expectedImpactLeft = (belowLeft?.expectedImpact || 0) * leftMultiplier;
                gateState.expectedImpactRight = (belowRight?.expectedImpact || 0) * rightMultiplier;

                // Add expected costs
                const costLeft =
                    (!belowLeft?.closed &&
                        (belowLeft?.bounce
                            ? gateState.costValue / 2
                            : (belowLeft?.expectedCosts || 0) * leftMultiplier)) ||
                    0;
                const costRight =
                    (!belowRight?.closed &&
                        (belowRight?.bounce
                            ? gateState.costValue / 2
                            : (belowRight?.expectedCosts || 0) * rightMultiplier)) ||
                    0;
                gateState.expectedCosts += costLeft + costRight;
            } else {
                // From impact values
                gateState.expectedImpactLeft = impactValues[gateState.column] / 2;
                gateState.expectedImpactRight = impactValues[gateState.column + 1] / 2;
            }

            // Get expected impact, profit and metric value
            gateState.expectedImpact = gateState.expectedImpactLeft + gateState.expectedImpactRight;
            gateState.expectedProfit = getProfitValue(metric, gateState.expectedImpact, gateState.expectedCosts);
            gateState.expectedMetricValue = getMetricValue(metric, gateState.expectedImpact, gateState.expectedCosts);
        }
    }

    return mapping;
}
